import { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { X, ChevronDown, ImagePlus, Loader2, RefreshCw, ExternalLink, Check } from 'lucide-react';
import './RemixVideoModal.css';
import { remixVideo, uploadElementImage } from '../lib/api';
import { cssAspectRatio, prettyModelLabel } from '../lib/videoUi';

const DEFAULT_REMIX_MODEL = 'gemini-omni-flash-edit';
const MAX_ATTACHMENTS = 4;

const acceptsMax = (model, group, key) => {
  const groupObj = model?.accepts?.[group];
  const target = key ? groupObj?.[key] : groupObj;
  const value = Number(target?.max);
  return Number.isFinite(value) ? value : 0;
};

/**
 * Modal for remixing an existing generated video: prompt + optional reference
 * images, powered by any video-to-video capable model (Google Omni by default).
 */
function RemixVideoModal({ video, modelSpecs, onClose, onRemixStarted }) {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [selectedModelId, setSelectedModelId] = useState(null);
  const [attachments, setAttachments] = useState([]); // { file, previewUrl }
  const [keepAudio, setKeepAudio] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  // Release object URLs when the modal unmounts.
  useEffect(() => {
    return () => {
      attachments.forEach((a) => URL.revokeObjectURL(a.previewUrl));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Models that can take a source video (i.e. can remix).
  const remixModels = useMemo(() => {
    const models = Array.isArray(modelSpecs?.models) ? modelSpecs.models : [];
    return models.filter((m) => !m.agentOnly && acceptsMax(m, 'videos') >= 1);
  }, [modelSpecs]);

  const selectedModel = useMemo(() => {
    if (remixModels.length === 0) return null;
    const byId = (id) => remixModels.find((m) => (m.id || m.alias) === id);
    return byId(selectedModelId) || byId(DEFAULT_REMIX_MODEL) || remixModels[0];
  }, [remixModels, selectedModelId]);

  const maxReferenceImages = Math.min(
    MAX_ATTACHMENTS,
    acceptsMax(selectedModel, 'images', 'reference')
  );
  const modelSupportsImages = maxReferenceImages > 0;

  const handleAttachFiles = (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith('image/'));
    if (files.length === 0) return;
    setAttachments((prev) => {
      const next = [...prev];
      for (const file of files) {
        if (next.length >= MAX_ATTACHMENTS) break;
        next.push({ file, previewUrl: URL.createObjectURL(file) });
      }
      return next;
    });
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => {
      const target = prev[index];
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSubmit = async () => {
    if (!prompt.trim() || isSubmitting || !selectedModel) return;
    if (attachments.length > 0 && !modelSupportsImages) {
      setError(
        `${prettyModelLabel(selectedModel.label, selectedModel.alias)} can't take reference images. Remove them or pick a model that supports image references.`
      );
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      let imageUrls = [];
      if (attachments.length > 0) {
        imageUrls = await Promise.all(
          attachments
            .slice(0, maxReferenceImages)
            .map((a) => uploadElementImage(video.projectId, a.file))
        );
      }

      const response = await remixVideo(video.id, {
        prompt: prompt.trim(),
        model: selectedModel.alias || selectedModel.id,
        imageUrls,
        keepAudio,
      });

      const created = response?.video;
      onRemixStarted(
        created
          ? {
              id: created.id,
              projectId: video.projectId,
              projectName: video.projectName,
              name: created.name,
              prompt: created.prompt,
              status: created.status || 'generating',
              videoUrl: null,
              durationMs: created.duration,
              aspectRatio: video.aspectRatio,
              type: 'video-to-video',
              model: created.model,
              modelLabel: prettyModelLabel(selectedModel.label, selectedModel.alias),
              sourceVideoId: video.id,
              createdAt: created.createdAt || new Date().toISOString(),
            }
          : null
      );
    } catch (err) {
      console.error('Failed to start remix:', err);
      setError('Failed to start remix. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="remix-modal-overlay" onClick={() => !isSubmitting && onClose()}>
      <div className="remix-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="remix-modal__close"
          onClick={onClose}
          disabled={isSubmitting}
          aria-label="Close"
        >
          <X size={16} />
        </button>

        <div className="remix-modal__preview">
          <video
            className="remix-modal__video"
            style={{ aspectRatio: cssAspectRatio(video.aspectRatio) }}
            src={video.videoUrl}
            controls
            playsInline
            preload="metadata"
          />
        </div>

        <div className="remix-modal__panel">
          <div className="remix-modal__heading">
            <h2 className="remix-modal__title">Remix this video</h2>
            <p className="remix-modal__subtitle">
              Describe the change you want — swap the style, the setting, the subject, anything.
            </p>
          </div>

          {video.prompt && (
            <div className="remix-modal__source">
              <span className="remix-modal__source-label">Original prompt</span>
              <p className="remix-modal__source-prompt">{video.prompt}</p>
            </div>
          )}

          <textarea
            ref={textareaRef}
            className="remix-modal__textarea"
            placeholder='e.g. "Make it nighttime with neon lights" or "Turn the car into a vintage convertible"'
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={4}
            disabled={isSubmitting}
          />

          {['klingo3-standard-edit', 'klingo3-pro-edit'].includes(selectedModel?.alias || selectedModel?.id) && (
            <label className="remix-modal__keep-audio">
              <input
                type="checkbox"
                checked={keepAudio}
                onChange={(event) => setKeepAudio(event.target.checked)}
                disabled={isSubmitting}
              />
              <span>Keep source audio</span>
            </label>
          )}

          {attachments.length > 0 && (
            <div className="remix-modal__attachments">
              {attachments.map((attachment, index) => (
                <div key={attachment.previewUrl} className="remix-modal__attachment">
                  <img src={attachment.previewUrl} alt={`Reference ${index + 1}`} />
                  <button
                    type="button"
                    className="remix-modal__attachment-remove"
                    onClick={() => removeAttachment(index)}
                    disabled={isSubmitting}
                    aria-label="Remove image"
                  >
                    <X size={11} />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="remix-modal__controls">
            <button
              type="button"
              className="remix-modal__attach-button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isSubmitting || attachments.length >= MAX_ATTACHMENTS}
              title={
                modelSupportsImages
                  ? 'Attach reference images'
                  : 'Attach reference images (pick an image-capable model to use them)'
              }
            >
              <ImagePlus size={15} />
              <span>Add images</span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(e) => {
                handleAttachFiles(e.target.files);
                e.target.value = '';
              }}
            />

            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button type="button" className="remix-modal__model-trigger" disabled={isSubmitting}>
                  {selectedModel?.modelThumbnail && (
                    <img
                      className="remix-modal__model-thumb"
                      src={selectedModel.modelThumbnail}
                      alt=""
                    />
                  )}
                  <span className="remix-modal__model-label">
                    {selectedModel
                      ? prettyModelLabel(selectedModel.label, selectedModel.alias)
                      : 'Loading models…'}
                  </span>
                  <ChevronDown size={14} />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content className="remix-modal__model-menu" sideOffset={6} align="end">
                  {remixModels.map((model) => {
                    const id = model.id || model.alias;
                    const isSelected = selectedModel && (selectedModel.id || selectedModel.alias) === id;
                    const supportsImages = acceptsMax(model, 'images', 'reference') > 0;
                    const blockedByImages = attachments.length > 0 && !supportsImages;
                    return (
                      <DropdownMenu.Item
                        key={id}
                        className={`remix-modal__model-item${blockedByImages ? ' remix-modal__model-item--disabled' : ''}`}
                        disabled={blockedByImages}
                        onSelect={() => setSelectedModelId(id)}
                      >
                        {model.modelThumbnail && (
                          <img className="remix-modal__model-thumb" src={model.modelThumbnail} alt="" />
                        )}
                        <div className="remix-modal__model-item-copy">
                          <span className="remix-modal__model-item-label">
                            {prettyModelLabel(model.label, model.alias)}
                          </span>
                          <span className="remix-modal__model-item-desc">
                            {supportsImages ? 'Video + image references' : 'Video + prompt'}
                          </span>
                        </div>
                        {isSelected && <Check size={14} className="remix-modal__model-check" />}
                      </DropdownMenu.Item>
                    );
                  })}
                  {remixModels.length === 0 && (
                    <div className="remix-modal__model-empty">No remix-capable models available</div>
                  )}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>

          {attachments.length > 0 && !modelSupportsImages && (
            <p className="remix-modal__hint">
              {selectedModel
                ? `${prettyModelLabel(selectedModel.label, selectedModel.alias)} takes a prompt only — switch models to use your images.`
                : ''}
            </p>
          )}

          {error && <div className="remix-modal__error">{error}</div>}

          <div className="remix-modal__footer">
            {video.projectId && (
              <button
                type="button"
                className="remix-modal__project-link"
                onClick={() => navigate(`/project/${video.projectId}`)}
              >
                <ExternalLink size={13} />
                Open in project
              </button>
            )}
            <button
              type="button"
              className="remix-modal__submit"
              onClick={handleSubmit}
              disabled={isSubmitting || !prompt.trim() || !selectedModel}
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={15} className="remix-modal__submit-spinner" />
                  Starting remix…
                </>
              ) : (
                <>
                  <RefreshCw size={15} />
                  Remix
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RemixVideoModal;
